import type { TFunction } from 'i18next';
import { z } from 'zod';
import type { GetWebFilterResponse } from '@/api/generated/schemas';
import {
  WebFilterCriterionKind,
  WebFilterDateRangeMode,
  WebFilterPlacementKind,
  WebFilterPlacementSection,
} from '@/api/generated/schemas';
import { emptyPlacements, type PlacementFormValue } from '../internal/placementDefaults';
import { parseCriterionValues, sortCriteria } from '../internal/criterionModel';

const placementSchema = z.object({
  id: z.uuid().nullable(),
  section: z.enum([
    WebFilterPlacementSection.MainMenu,
    WebFilterPlacementSection.SearchFilter,
    WebFilterPlacementSection.HomePage,
  ]),
  kind: z.enum([
    WebFilterPlacementKind.MenuBanner,
    WebFilterPlacementKind.MenuTile,
    WebFilterPlacementKind.MenuSpecial,
    WebFilterPlacementKind.MenuLink,
    WebFilterPlacementKind.SearchSpecialPeriods,
    WebFilterPlacementKind.SearchTourTypes,
    WebFilterPlacementKind.HomeFindTour,
  ]),
  isEnabled: z.boolean(),
  labelText: z.string(),
  showPriceFrom: z.boolean(),
  description: z.string(),
  photoFileId: z.number().int().positive().nullable(),
  payload: z.record(z.string(), z.unknown()),
});

const criterionSchema = z.object({
  kind: z.enum([
    WebFilterCriterionKind.Transport,
    WebFilterCriterionKind.Nights,
    WebFilterCriterionKind.Destination,
    WebFilterCriterionKind.ProductTypes,
    WebFilterCriterionKind.Departure,
    WebFilterCriterionKind.Products,
    WebFilterCriterionKind.Price,
    WebFilterCriterionKind.Seats,
    WebFilterCriterionKind.DaysBeforeDeparture,
    WebFilterCriterionKind.PublishedDays,
    WebFilterCriterionKind.Dates,
  ]),
  values: z.union([
    z.object({ ids: z.array(z.union([z.string(), z.number()])) }),
    z.object({ from: z.number().nullable(), to: z.number().nullable() }),
    z.object({
      ranges: z.array(
        z.object({
          mode: z.enum([WebFilterDateRangeMode.Overlap, WebFilterDateRangeMode.Departures]),
          from: z.string(),
          to: z.string(),
          label: z.string().nullable(),
        })
      ),
      weekend: z.boolean(),
      holiday: z.boolean(),
    }),
  ]),
});

export function buildWebFilterFormSchema(t: TFunction) {
  return z
    .object({
      name: z.string().trim().min(1, t('webFilter.validation.nameRequired')),
      webProjectId: z.number().int().positive(t('webFilter.validation.webProjectRequired')),
      webProjectName: z.string(),
      iconFileId: z.number().int().positive().nullable(),
      sectionMenuEnabled: z.boolean(),
      sectionSearchEnabled: z.boolean(),
      sectionHpEnabled: z.boolean(),
      placements: z.array(placementSchema),
      criteria: z.array(criterionSchema),
    })
    .superRefine((value, ctx) => {
      const enabledSearch = value.placements.filter(
        (p) =>
          p.isEnabled &&
          (p.kind === WebFilterPlacementKind.SearchSpecialPeriods ||
            p.kind === WebFilterPlacementKind.SearchTourTypes)
      );
      if (value.sectionSearchEnabled && enabledSearch.length > 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['placements'],
          message: t('webFilter.validation.searchXor'),
        });
      }
    });
}

export type WebFilterFormValues = z.infer<ReturnType<typeof buildWebFilterFormSchema>>;

export function emptyWebFilterFormValues(
  webProjectId: number,
  webProjectName: string
): WebFilterFormValues {
  return {
    name: '',
    webProjectId,
    webProjectName,
    iconFileId: null,
    sectionMenuEnabled: false,
    sectionSearchEnabled: false,
    sectionHpEnabled: false,
    placements: emptyPlacements(),
    criteria: [],
  };
}

export function fromGetWebFilterResponse(response: GetWebFilterResponse): WebFilterFormValues {
  const base = emptyPlacements();
  const byKind = new Map(response.placements.map((p) => [p.kind, p]));

  const placements: PlacementFormValue[] = base.map((empty) => {
    const fromApi = byKind.get(empty.kind);
    if (!fromApi) return empty;
    return {
      id: fromApi.id,
      section: fromApi.section,
      kind: fromApi.kind,
      isEnabled: fromApi.isEnabled,
      labelText: fromApi.labelText ?? '',
      showPriceFrom: fromApi.showPriceFrom,
      description: fromApi.description ?? '',
      photoFileId: fromApi.photoFileId ?? null,
      payload:
        fromApi.payload && typeof fromApi.payload === 'object' && !Array.isArray(fromApi.payload)
          ? (fromApi.payload as Record<string, unknown>)
          : empty.payload,
    };
  });

  const menuEnabled = placements.some(
    (p) => p.isEnabled && p.section === WebFilterPlacementSection.MainMenu
  );
  const searchEnabled = placements.some(
    (p) => p.isEnabled && p.section === WebFilterPlacementSection.SearchFilter
  );
  const hpEnabled = placements.some(
    (p) => p.isEnabled && p.section === WebFilterPlacementSection.HomePage
  );

  return {
    name: response.name,
    webProjectId: response.webProjectId,
    webProjectName: response.webProjectName,
    iconFileId: response.iconFileId ?? null,
    sectionMenuEnabled: menuEnabled,
    sectionSearchEnabled: searchEnabled,
    sectionHpEnabled: hpEnabled,
    placements,
    criteria: sortCriteria(
      (response.criteria ?? []).map((item) => ({
        kind: item.kind,
        values: parseCriterionValues(item.kind, item.values),
      }))
    ),
  };
}
